import { useMemo } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import Philosophy from './components/Philosophy';
import Testimonials from './components/Testimonials';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Access from './components/Access';
import Footer from './components/Footer';
import SEOHead from '../../components/feature/SEOHead';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string) || 'https://example.com';

export default function Home() {
  const schemas = useMemo(() => {
    const localBusiness = {
      '@context': 'https://schema.org',
      '@type': 'LocalBusiness',
      name: 'まごころサービス',
      description:
        '介護保険外サービスを提供する事業。日常生活支援、心のケア、家事援助など、ご利用者様一人ひとりを家族のように大切にし、温かく寄り添います。',
      url: SITE_URL,
      telephone: '090-7289-8910',
      priceRange: '¥3,850〜/時間',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '金井ヶ丘4-8',
        addressLocality: '町田市',
        addressRegion: '東京都',
        postalCode: '194-0036',
        addressCountry: 'JP',
      },
      geo: {
        '@type': 'GeoCoordinates',
        latitude: 35.5678901,
        longitude: 139.4456789,
      },
      openingHoursSpecification: [
        {
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
          opens: '08:00',
          closes: '18:00',
        },
      ],
      areaServed: {
        '@type': 'AdministrativeArea',
        name: '東京都町田市周辺',
      },
      inLanguage: 'ja',
      currenciesAccepted: 'JPY',
      paymentAccepted: '現金',
    };

    const webSite = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'まごころサービス',
      url: SITE_URL,
      inLanguage: 'ja',
    };

    const organization = {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: 'まごころサービス',
      url: SITE_URL,
      telephone: '090-7289-8910',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '金井ヶ丘4-8',
        addressLocality: '町田市',
        addressRegion: '東京都',
        postalCode: '194-0036',
        addressCountry: 'JP',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '090-7289-8910',
        contactType: 'customer support',
        areaServed: 'JP',
        availableLanguage: 'Japanese',
      },
      areaServed: {
        '@type': 'AdministrativeArea',
        name: '東京都町田市周辺',
      },
    };

    const faqPage = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: '介護保険外サービスとは何ですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '介護保険の適用範囲外で提供されるサービスです。介護保険では対応できない日常生活のサポートや、お話し相手、買い物の付き添いなど、より柔軟で幅広いサービスをご利用いただけます。',
          },
        },
        {
          '@type': 'Question',
          name: '利用するにはどのような手続きが必要ですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'まずはお電話・メール・LINEでお気軽にご相談ください。ご要望をお伺いした上で、サービス内容やお見積もりをご提案いたします。ご納得いただけましたら、ご契約・サービス開始となります。',
          },
        },
        {
          '@type': 'Question',
          name: '料金はどのくらいかかりますか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'サービスの内容やご利用時間によって異なります。無料相談にてお見積もりをお出ししておりますので、まずはお気軽にお問い合わせください。ご予算に合わせたプランもご提案可能です。',
          },
        },
        {
          '@type': 'Question',
          name: '急な依頼にも対応してもらえますか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'はい、可能な限り対応いたします。スタッフの空き状況によりますが、当日のご依頼にも柔軟に対応できるよう体制を整えております。まずはお電話にてご相談ください。',
          },
        },
        {
          '@type': 'Question',
          name: 'サービスの対応エリアはどこですか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '現在、地域密着型でサービスを提供しております。対応エリアの詳細についてはお問い合わせください。エリア外の場合でも、ご相談に応じて対応できる場合がございます。',
          },
        },
        {
          '@type': 'Question',
          name: 'どのようなスタッフが来てくれますか？',
          acceptedAnswer: {
            '@type': 'Answer',
            text: '介護や福祉の経験を持つ、研修を受けた専門スタッフがお伺いします。ご利用者様との相性も大切にしており、担当スタッフの変更にも柔軟に対応いたします。',
          },
        },
      ],
    };

    return [localBusiness, webSite, organization, faqPage];
  }, []);

  return (
    <div className="min-h-screen">
      <SEOHead
        title="まごころサービス | 介護保険外サービス | 心のこもったケアと生活支援"
        description="まごころサービスは、介護保険外サービスを提供する事業です。日常生活支援、心のケア、家事援助など、ご利用者様一人ひとりを家族のように大切にし、温かく寄り添います。高齢者やそのご家族に安心と笑顔をお届けします。無料相談受付中。"
        canonicalPath="/"
        schemas={schemas}
      />
      <Hero />
      <Services />
      <Philosophy />
      <Testimonials />
      <FAQ />
      <Contact />
      <Access />
      <Footer />
    </div>
  );
}
